import { colorSetting } from "../../../constant/color";
import { extractStops } from "../../../utils/projectAmbient";

/** BE の get_banner_color_style と同じフォールバック先 */
const DEFAULT_BANNER_COLOR_KEY = "1";

/** `banner_color`（"1"〜"18"）→ colorSetting のエントリ（未知値はフォールバック） */
const findBannerSetting = (bannerColor?: string | null) => {
  const trimmed = bannerColor == null ? "" : String(bannerColor).trim();
  const key = trimmed === "" ? DEFAULT_BANNER_COLOR_KEY : trimmed;
  return (
    colorSetting.find((c) => String(c.id) === key) ??
    colorSetting.find((c) => String(c.id) === DEFAULT_BANNER_COLOR_KEY)
  );
};

/**
 * `banner_color`（"1"〜"18"）から、そのプロジェクトのバナー背景をそのまま返す。
 *
 * バナー定義（`constant/color.js` の colorSetting）を直接引くので、単色でも
 * グラデーションでも実際のバナーと同じ見た目になり、独自の色表を持たずに済む
 * （`projectAmbient` と同じ方針）。
 */
export const projectBannerBackground = (
  bannerColor?: string | null,
): string =>
  findBannerSetting(bannerColor)?.css_code?.background ?? "var(--soremo-gray)";

/**
 * バナー背景の上に置く文字色。colorSetting の `text_color_css`
 * （淡色バナーのみ "black"）に従う。
 */
export const projectBannerTextColor = (bannerColor?: string | null): string =>
  findBannerSetting(bannerColor)?.text_color_css === "black"
    ? "var(--soremo-black)"
    : "#fff";

/**
 * 白背景上の枠線・文字に使える不透明の代表色（アクセント）の明るさ上限。
 * これより明るい（平均チャンネル値が大きい）色は黒を混ぜて沈め、可読性を保つ。
 */
const ACCENT_LUMA_CAP = 168;

/**
 * `banner_color` から、白背景上の枠線・文字に使える**単色**の代表色を返す。
 * グラデーションバナーは中央の色 stop を採用する（projectAmbient と同じ考え方）。
 * 淡色バナーはそのままでは白地で読めないため、明度を上限まで下げる。
 */
export const projectBannerAccent = (bannerColor?: string | null): string => {
  const stops = extractStops(projectBannerBackground(bannerColor));
  if (stops.length === 0) return "var(--soremo-gray)";
  const mid = stops[Math.floor((stops.length - 1) / 2)];
  const mean = (mid.r + mid.g + mid.b) / 3;
  if (mean <= ACCENT_LUMA_CAP) return `rgb(${mid.r}, ${mid.g}, ${mid.b})`;
  const k = ACCENT_LUMA_CAP / mean;
  return `rgb(${Math.round(mid.r * k)}, ${Math.round(mid.g * k)}, ${Math.round(
    mid.b * k,
  )})`;
};
